import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { PlusIcon, TrashIcon, PencilIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { uploadImage, buildCloudinaryFolder } from '../../../utils/cloudinary'

function ProductsForm({ products, onProductsChange, userId, vcardId }) {
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    desc: '',
    price: '',
    imageUrl: '',
    order: ''
  })

  const handleAddProduct = () => {
    if (!formData.name.trim()) {
      return
    }

    const newProduct = {
      id: Date.now().toString(),
      name: formData.name.trim(),
      desc: formData.desc.trim(),
      imageUrl: formData.imageUrl.trim(),
      order: (formData.order !== '' && !isNaN(parseInt(formData.order, 10))) ? parseInt(formData.order, 10) : products.length
    }

    if (formData.price !== '' && !isNaN(parseFloat(formData.price))) {
      newProduct.price = parseFloat(formData.price)
    }

    onProductsChange([...products, newProduct])
    resetForm()
  }

  const handleEditProduct = () => {
    if (!formData.name.trim()) {
      return
    }

    const updatedProducts = products.map(product =>
      product.id === editingProduct.id
        ? {
            ...product,
            name: formData.name.trim(),
            desc: formData.desc.trim(),
            price: formData.price !== '' && !isNaN(parseFloat(formData.price)) ? parseFloat(formData.price) : undefined,
            imageUrl: formData.imageUrl.trim()
          }
        : product
    )

    onProductsChange(updatedProducts)
    resetForm()
  }

  const handleDeleteProduct = (productId) => {
    if (window.confirm('¿Estás seguro de eliminar este producto?')) {
      onProductsChange(products.filter(product => product.id !== productId))
    }
  }

  const startEditProduct = (product) => {
    setEditingProduct(product)
    setFormData({
      name: product.name,
      desc: product.desc || '',
      price: product.price !== undefined ? String(product.price) : '',
      imageUrl: product.imageUrl || '',
      order: typeof product.order === 'number' ? String(product.order) : ''
    })
    setShowAddForm(true)
  }

  const resetForm = () => {
    setFormData({
      name: '',
      desc: '',
      price: '',
      imageUrl: '',
      order: ''
    })
    setShowAddForm(false)
    setEditingProduct(null)
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]
    if (file && userId && vcardId) {
      try {
        // Build folder path for Cloudinary
        const folder = buildCloudinaryFolder(userId, vcardId, 'products')
        
        // Upload to Cloudinary
        const { url } = await uploadImage(file, folder)
        
        // Update form with Cloudinary URL
        setFormData({ ...formData, imageUrl: url })
      } catch (error) {
        console.error('Error uploading image:', error)
        alert('Error al subir la imagen: ' + error.message)
      }
    } else if (!userId || !vcardId) {
      alert('Error: No se puede subir la imagen. Falta información del usuario o Dkard.')
    }
  }

  return (
    <div className="space-y-6">
      {/* Existing products */}
      {products.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Tus Productos ({products.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product) => (
              <div key={product.id} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="aspect-w-1 aspect-h-1 mb-3 relative">
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className="w-full h-32 object-cover rounded"
                    />
                  ) : (
                    <div className="w-full h-32 bg-gray-100 rounded flex items-center justify-center">
                      <span className="text-gray-400 text-sm">Sin imagen</span>
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        const idx = products.findIndex(p => p.id === product.id)
                        if (idx > 0) {
                          const arr = [...products]
                          ;[arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]]
                          const withOrder = arr.map((p, i) => ({ ...p, order: i }))
                          onProductsChange(withOrder)
                        }
                      }}
                      className="w-8 h-8 rounded-full bg-white/90 border border-gray-200 flex items-center justify-center shadow-sm"
                      aria-label="Mover arriba"
                    >
                      <ChevronUpIcon className="w-4 h-4 text-gray-700" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const idx = products.findIndex(p => p.id === product.id)
                        if (idx < products.length - 1) {
                          const arr = [...products]
                          ;[arr[idx + 1], arr[idx]] = [arr[idx], arr[idx + 1]]
                          const withOrder = arr.map((p, i) => ({ ...p, order: i }))
                          onProductsChange(withOrder)
                        }
                      }}
                      className="w-8 h-8 rounded-full bg-white/90 border border-gray-200 flex items-center justify-center shadow-sm"
                      aria-label="Mover abajo"
                    >
                      <ChevronDownIcon className="w-4 h-4 text-gray-700" />
                    </button>
                  </div>
                </div>
                
                <h4 className="font-semibold text-gray-900 mb-1">{product.name}</h4>
                {product.desc && (
                  <p className="text-sm text-gray-600 mb-2">{product.desc}</p>
                )}
                <p className="text-lg font-bold text-green-600">Bs. {product.price}</p>
                
                <div className="flex justify-between items-center mt-3 pt-3 border-t">
                  <button
                    type="button"
                    onClick={() => startEditProduct(product)}
                    className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
                  >
                    <PencilIcon className="w-4 h-4 mr-1" />
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteProduct(product.id)}
                    className="flex items-center text-red-600 hover:text-red-800 text-sm"
                  >
                    <TrashIcon className="w-4 h-4 mr-1" />
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add product form */}
      <div className="border-t pt-6">
        {!showAddForm ? (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Agregar Producto
          </button>
        ) : (
          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Producto *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="Ej: Camiseta Premium"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  value={formData.desc}
                  onChange={(e) => setFormData({ ...formData, desc: e.target.value })}
                  rows={3}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="Describe tu producto..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Precio
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">Bs.</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="block w-full pl-8 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="29.99"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Orden</label>
                <input
                  type="number"
                  min="0"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="0, 1, 2..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Imagen del Producto
                </label>
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {formData.imageUrl && (
                    <div className="flex items-center space-x-2">
                      <img
                        src={formData.imageUrl}
                        alt="Preview"
                        className="w-16 h-16 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, imageUrl: '' })}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Eliminar imagen
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={editingProduct ? handleEditProduct : handleAddProduct}
                disabled={!formData.name.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {editingProduct ? 'Guardar Cambios' : 'Agregar Producto'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tips */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-amber-900 mb-2">💡 Consejos para tus productos:</h4>
        <ul className="text-sm text-amber-800 space-y-1">
          <li>• Usa imágenes de alta calidad para mejorar las ventas</li>
          <li>• Sé específico en las descripciones de tus productos</li>
          <li>• Los precios claros ayudan a los clientes a decidir</li>
          <li>• Mantén tu catálogo actualizado con productos disponibles</li>
        </ul>
      </div>
    </div>
  )
}

ProductsForm.propTypes = {
  products: PropTypes.array.isRequired,
  onProductsChange: PropTypes.func.isRequired,
  userId: PropTypes.string,
  vcardId: PropTypes.string,
}

export default ProductsForm
